import request from '@/utils/api/request';
import downloadByLink from '@/utils/download-by-link';
import { getFileIdAfterUploading } from '@/utils/getFileIdAfterUploading';
import t from '@/i18n';
import ChooseCurrencyWindow from '@/containers/incoming-tender/ChooseCurrencyWindow';
import router from '@/router';

const FORM_DEFAULT = {
  company_name: '',
  email: '',
  name: '',
  phone_number: '',
  description: '',
  currency: '',
};

export const state = () => ({
  publicTenderInfo: null,
  formHasBeenSubmitted: false,
  showModal: false,
  isExcelModal: true,
  submitLoading: false,
  tenderHash: '',
  excelFile: [],
  pdfFile: [],
  additionalFiles: [],
  file_ids: [],
  form: Object.assign({}, FORM_DEFAULT),
  errors: {},
  loadings: [],
  contactPerson: {},
  offerXlsxTemplateFilename: '',
  downloadXlsxError: '',
  isPublic: true,
});

export const mutations = {
  setTenderHash(state, tenderHash) {
    state.tenderHash = tenderHash;
  },
  setContactPerson(state, person) {
    state.contactPerson = person;
  },
  setOfferXlsxTemplateFilename(state, name) {
    state.offerXlsxTemplateFilename = name;
  },
  setPublicTenderInfo(state, info) {
    state.publicTenderInfo = info;
  },
  setModalShown(state, status) {
    state.showModal = status;
  },
  changeIsExcelModalStatus(state, status) {
    state.isExcelModal = status;
  },
  setErrors(state, errors) {
    state.errors = errors;
  },
  setErrorByType(state, { type, value }) {
    state.errors[type] = value;
  },
  clearError(state, type) {
    state.errors[type] = null;
  },
  changeFormHasBeenSubmittedStatus(state, status) {
    state.formHasBeenSubmitted = status;
  },
  setLoadings(state, loadings) {
    state.loadings = loadings;
  },
  setExcelFile(state, file) {
    state.excelFile = file;
  },
  setPdfFile(state, file) {
    state.pdfFile = file;
  },
  setAdditionalFiles(state, files) {
    state.additionalFiles = files;
  },
  removeAdditionalFileById(state, index) {
    state.additionalFiles.splice(index, 1);
  },
  setSubmitLoadingStatus(state, status) {
    state.submitLoading = status;
  },
  setFileIds(state, file_ids) {
    state.file_ids = file_ids;
  },
  setLoadingByType(state, { type, value }) {
    state.loadings[type] = value;
  },
  setOfferInfoToForm(state, offer) {
    state.form.company_name = offer?.contractor_team?.name || '';
    state.form.email = offer?.contractor_contact_person_user?.email || '';
    state.form.phone_number = offer?.contractor_contact_person_user?.phone_number || '';
  },
  setDownloadXlsxError(state, error) {
    state.downloadXlsxError = error?.response?.statusText || t('An error has occurred');
  },
  clearDownloadXlsxError(state) {
    state.downloadXlsxError = '';
  },
  setPublicStatus(state, value) {
    state.isPublic = value;
  },
  clearForm(state) {
    state.form = Object.assign({}, FORM_DEFAULT);
  },
};

export const actions = {
  async initPublicTenderInfo({ commit }, { tenderHash, offerHash, isPublic = true }) {
    commit('setTenderHash', tenderHash);
    try {
      let config = null;
      if (offerHash) {
        config = { params: { offer_hash: offerHash } };
      }
      const { data } = await request.get(`/public/tenders/${tenderHash}/`, config);
      commit('setPublicTenderInfo', data);
      commit('setContactPerson', data?.contact_person_responsible_user);
      commit('setOfferXlsxTemplateFilename', data?.offer_xlsx_template_filename);
      if (data.offer) {
        commit('setOfferInfoToForm', data.offer);
      }
      commit('setPublicStatus', isPublic);
    } catch (e) {
      console.error(e);
      if (e.response.status === 404) {
        await router.push({ name: '404' });
      }
    }
  },
  openExcelModal({ commit }) {
    commit('changeIsExcelModalStatus', true);
    commit('setModalShown', true);
  },
  openPdfModal({ commit }) {
    commit('changeIsExcelModalStatus', false);
    commit('setModalShown', true);
  },
  closeModal({ commit }) {
    commit('setModalShown', false);
    commit('setErrors', {});
    commit('changeFormHasBeenSubmittedStatus', false);
    commit('clearForm');
  },
  async downloadXlsxHandler({ dispatch, commit, state }, { loadingId }) {
    const fileName = state.publicTenderInfo.offer_xlsx_template_filename;
    commit('clearDownloadXlsxError');
    dispatch(
      'modal/openModal',
      {
        component: ChooseCurrencyWindow,
        isNested: true,
        context: {
          title: t('Choose currency'),
          description: t('We will tailor cells, so prices will be in currency you specify here.'),
          emitter: ({ currency }) => {
            dispatch('downloadFileHandler', {
              route: `/public/tenders/${state.tenderHash}/offer_xlsx_template/`,
              loadingId,
              currency,
              fileName,
            });
            dispatch('modal/closeModal', {}, { root: true });
          },
        },
      },
      { root: true }
    );
  },
  async downloadAllFilesHandler({ dispatch, state }, loadingId) {
    dispatch('downloadFileHandler', {
      route: `/public/tenders/${state.tenderHash}/files_zip/`,
      loadingId,
    });
  },
  async downloadFileHandler({ commit, state }, { route, loadingId, currency, fileName }) {
    commit('setLoadings', [...state.loadings, loadingId]);
    const response = await downloadByLink({ link: route, query: { currency }, fileName });
    if (response instanceof Error) {
      commit('setDownloadXlsxError', response);
    }
    const updatedLoadings = state.loadings.filter((id) => id !== loadingId);
    commit('setLoadings', updatedLoadings);
  },
  async handleFileInput({ commit }, { files, field }) {
    switch (field) {
      case 'excel':
        commit('setErrorByType', {
          type: 'proposal_file',
          value: null,
        });
        commit('setExcelFile', files || []);
        break;
      case 'pdf':
        commit('setPdfFile', files || []);
        break;
      case 'additional':
        commit('setAdditionalFiles', files || []);
    }
  },
  handleFileDelete({ commit }, { field, idx = -1 }) {
    switch (field) {
      case 'excel':
        commit('setErrorByType', {
          type: 'proposal_file',
          value: null,
        });
        commit('setExcelFile', []);
        break;
      case 'pdf':
        commit('setPdfFile', []);
        break;
      case 'additional':
        if (idx > -1) {
          commit('removeAdditionalFileById', idx);
        }
    }
  },
  async submitOffer({ commit, state, dispatch }) {
    commit('setErrors', {});
    commit('setSubmitLoadingStatus', true);
    if (state.isExcelModal) {
      if (!state.excelFile.length) {
        commit('setErrorByType', {
          type: 'proposal_file',
          value: [t('Please upload Excel file')],
        });
        commit('setSubmitLoadingStatus', false);
        return;
      }
    }
    if (!state.isExcelModal) {
      if (!state.pdfFile.length) {
        commit('setErrorByType', {
          type: 'proposal_file',
          value: [t('Please upload a PDF file')],
        });
        commit('setSubmitLoadingStatus', false);
        return;
      }
    }
    try {
      commit('setLoadingByType', { type: 'additionalFilesUpload', value: true });
      for (let i = 0; i < state.additionalFiles.length; i++) {
        let id = await getFileIdAfterUploading({
          file: state.additionalFiles[i],
          url: `/public/tenders/${state.tenderHash}/offer_version_file/`,
        });
        if (id) {
          commit('setFileIds', [...state.file_ids, id]);
        }
      }
      if (!state.isExcelModal && state.pdfFile.length) {
        let id = await getFileIdAfterUploading({
          file: state.pdfFile[0],
          url: `/public/tenders/${state.tenderHash}/offer_version_file/`,
        });
        if (id) {
          commit('setFileIds', [...state.file_ids, id]);
        }
      }
    } catch (e) {
      commit('setErrorByType', {
        type: 'additional_file',
        value: [t('Additional files contains errors')],
      });
      commit('setAdditionalFiles', []);
      commit('setSubmitLoadingStatus', false);
      return;
    } finally {
      commit('setLoadingByType', { type: 'additionalFilesUpload', value: false });
    }
    try {
      let fd = new FormData();
      fd.set('company_name', state.form.company_name);
      fd.set('description', state.form.description);
      fd.set('email', state.form.email);
      fd.set('name', state.form.name);
      fd.set('phone_number', state.form.phone_number);
      fd.set('currency', state.form.currency);
      if (state.file_ids.length) {
        fd.set('file_ids', JSON.stringify(state.file_ids));
      }
      if (state.isExcelModal && state.excelFile.length) {
        fd.set('proposal_file', state.excelFile[0]);
      }
      if (state.isPublic) {
        await request.post(`/public/tenders/${state.tenderHash}/submit_offer/`, fd);
      } else {
        await request.post(`/tenders/${state.publicTenderInfo.id}/offers/`, fd);
      }
      dispatch(
        'alert/showAlert',
        {
          type: 'success',
          text: t('Thank you! Your offer has been received!'),
        },
        { root: true }
      );
      dispatch('closeModal');
      commit('setExcelFile', []);
      commit('setPdfFile', []);
      commit('setAdditionalFiles', []);
    } catch (e) {
      if (
        e.response.status === 500 ||
        e.response?.data?.detail ||
        e.response?.data?.non_field_errors
      ) {
        const error =
          e.response?.data?.detail ||
          e.response?.data?.non_field_errors?.[0] ||
          t('Unknown error, please try again');
        commit('setErrorByType', { type: 'modalError', value: error });
      } else {
        commit('setErrors', e.response?.data);
      }
    }
    commit('setFileIds', []);
    commit('setSubmitLoadingStatus', false);
  },
};

export const getters = {
  contactName(state) {
    return state.contactPerson?.name;
  },
  contactPhoneNumber(state) {
    return state.contactPerson?.phone_number;
  },
  contactEmail(state) {
    return state.contactPerson?.email;
  },
  contactPosition(state) {
    return state.contactPerson?.position;
  },
  contactAvatar(state) {
    return state.contactPerson?.files_urls?.avatar_thumbnail;
  },
};

export default { namespaced: true, state, mutations, actions, getters };
